// Allowed websites: the calculator only runs on these domains (anywhere else it
// stays hidden). Add (tidied: "https://www.Site.co.uk/quote" → "site.co.uk",
// validated, de-duplicated, up to 10) and remove. Used by the Install tab and
// the new-tool wizard's Websites step; the parent owns the list and saves it.
// Hosted pages don't need any: they run on our own go.<domain>.
import { useState, type FormEvent } from "react";
import { Plus, X } from "lucide-react";
import { Attention } from "./Attention";

export const MAX_DOMAINS = 10; // matches MAX_DOMAINS in 100-api validation
const HOSTNAME = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** Tidy what people paste: "https://www.Site.co.uk/quote" → "site.co.uk" (www is always allowed). */
export function cleanDomain(raw: string): string {
  const host = raw.trim().toLowerCase().replace(/^[a-z]+:\/\//, "").split("/")[0].split(":")[0];
  return host.startsWith("www.") ? host.slice(4) : host;
}

export function DomainsEditor({ domains, onChange, guide = false }: { domains: string[]; onChange: (domains: string[]) => void; guide?: boolean }) {
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  function add(e: FormEvent) {
    e.preventDefault();
    const d = cleanDomain(input);
    if (!HOSTNAME.test(d)) return setError("Enter a domain like installer.co.uk");
    if (domains.includes(d)) return setError(`${d} is already allowed`);
    if (domains.length >= MAX_DOMAINS) return setError(`Up to ${MAX_DOMAINS} websites per component`);
    onChange([...domains, d]);
    setInput("");
    setError(null);
  }

  return (
    <>
      <form className="domain-add" onSubmit={add}>
        <span className="has-attention notify-input">
          <input
            className="input"
            placeholder="installer.co.uk"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setError(null);
            }}
            aria-label="Domain to allow"
          />
          {guide && domains.length === 0 && !input.trim() && <Attention corner="tr" label="Add your website" />}
        </span>
        <span className="has-attention">
          <button type="submit" className="btn btn-secondary" disabled={!input.trim()}>
            <Plus size={16} aria-hidden="true" /> Add
          </button>
          {guide && input.trim() && <Attention corner="tr" label="Add this website" />}
        </span>
      </form>
      {error && <p className="price-error">{error}</p>}
      {domains.length > 0 ? (
        <ul className="domain-list">
          {domains.map((d) => (
            <li key={d}>
              <span>{d}</span>
              <button type="button" className="icon-btn" aria-label={`Remove ${d}`} title="Remove" onClick={() => onChange(domains.filter((x) => x !== d))}>
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted domain-empty">No websites yet: the calculator won't show anywhere until you add one.</p>
      )}
    </>
  );
}
